import styled, { css, keyframes } from 'styled-components';
import { fontTokens } from '../../tokens/globals/tokens';
import { accordionTokens } from '../../tokens/components/Accordion/tokens';
import {
  MOBILE_WIDTH,
  TABLET_LANDSCAPE_WIDTH,
  TABLET_PORTRAIT_WIDTH,
  DESKTOP_WIDTH,
} from '../../helpers/constants/responsive-breakpoints';
import { resetBrowserButton } from '../Button/styles';
import { StyledIcon } from '../Icon/Icon';

export const AccordionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${accordionTokens.WrapperSpacingBottomDefault};
  width: 100%;
  background: ${accordionTokens.WrapperBackgroundColorDefault};
  border-radius: ${accordionTokens.WrapperBorderRadiusDefault};
  border-style: ${accordionTokens.WrapperBorderStyleDefault};
  border-width: ${accordionTokens.WrapperBorderWidthDefault};
  border-color: ${accordionTokens.WrapperBorderColorDefault};
  overflow-y: hidden;

  ${({ theme }) =>
    !theme.accordionOpen &&
    css`
      &:hover {
        border-color: ${accordionTokens.WrapperBorderColorHover};
      }

      &:focus-visible {
        border-color: ${accordionTokens.WrapperBorderColorKeyboardFocus};
      }

      &:focus-visible {
        outline: ${accordionTokens.WrapperShadowWidthBaseKeyboardFocus} solid
          ${accordionTokens.WrapperShadowColorBaseKeyboardFocus};
      }
    `};
`;

export const AccordionHeader = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  gap: ${accordionTokens.HeaderSpacingGapDefaultDesktop};
  justify-content: space-between;
  padding-bottom: ${accordionTokens.HeaderSpacingPaddingDefaultBottom};
  padding-inline-start: ${({ theme }) =>
    theme.accordionTwiddlePlacement === 'end'
      ? accordionTokens.HeaderSpacingPaddingDefaultLeft
      : accordionTokens.HeaderSpacingPaddingCompactLeft};
  padding-right: ${accordionTokens.HeaderSpacingPaddingDefaultRight};
  padding-top: ${accordionTokens.HeaderSpacingPaddingDefaultTop};
  position: relative;
  transition: background-color 0.3s linear;

  ${({ theme }) =>
    theme.accordionOpen &&
    css`
      background-color: ${accordionTokens.HeaderBackgroundColorDefault};
    `};

  &:hover {
    cursor: pointer;
  }

  @media (max-width: ${MOBILE_WIDTH}px) {
    gap: ${accordionTokens.HeaderSpacingGapDefaultMobile};
  }
`;

export const AccordionTitle = styled.div`
  min-width: 160px;

  // Body text has different line-height than headings. We'll use
  // this temporary adjustment until we find a better solution or
  // maybe this component might change after v1
  h3 {
    line-height: ${fontTokens.GlobalLeadingBody3};
  }

  ${({ theme }) =>
    theme.accordionDescription &&
    (!theme.accordionOpen || theme.accordionAlwaysShowDescription) &&
    theme.fixedTitleWidth &&
    css`
      // needs fixed width to keep descriptions aligned
      width: 20%;

      @media (max-width: ${DESKTOP_WIDTH}px) {
        width: 25%;
      }

      @media (max-width: ${TABLET_LANDSCAPE_WIDTH}px) {
        width: auto;
      }
    `};
`;

export const AccordionInfo = styled.div`
  ${({ theme }) =>
    theme.headerVerticalAlignment &&
    css`
      align-items: ${theme.headerVerticalAlignment};
    `};

  display: flex;
  flex-direction: row;
  flex-grow: 1;
  gap: ${accordionTokens.HeaderSpacingGapDefaultDesktop};

  @media (max-width: ${TABLET_PORTRAIT_WIDTH}px) {
    flex-direction: column;
    gap: ${accordionTokens.HeaderSpacingGapDefaultTablet};
  }
`;

export const AccordionDescriptionWrapper = styled.summary`
  flex-grow: 1;
`;

export const AccordionClickHandler = styled.button`
  ${resetBrowserButton};
  background: none;
  padding-left: ${({ theme }) =>
    theme.accordionTwiddlePlacement === 'end'
      ? accordionTokens.HeaderSpacingPaddingDefaultLeft
      : accordionTokens.HandlerSpacingPaddingCompactLeft};
  padding-right: ${accordionTokens.HeaderSpacingPaddingDefaultRight};
  text-align: ${({ theme }) => (theme.accordionTwiddlePlacement === 'end' ? 'end' : 'start')};
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;

  ${StyledIcon} {
    transform: ${({ theme }) => (theme.accordionTwiddlePlacement === 'end' ? 'rotate(0deg)' : 'rotate(-90deg)')};
    transition: transform linear;
    transition-duration: ${({ theme }) => (theme.accordionTwiddlePlacement === 'end' ? '0.3s' : '0.15s')};
  }

  ${({ theme }) =>
    theme.accordionOpen &&
    css`
      ${StyledIcon} {
        transform: ${theme.accordionTwiddlePlacement === 'end' ? 'rotate(180deg)' : 'rotate(0deg)'};
      }
    `};
`;

const openContentAnimation = keyframes`
  from {
    max-height: 0;
  }
  to {
    max-height: 1000px;
    overflow-y: auto;
  }
`;

export const AccordionContentWrapper = styled.div`
  max-height: 0;
  overflow-y: hidden;

  // height auto can't be transitioned but we can transition max-height;
  // max-height needs to be set taller than we need to allow for transition
  ${({ theme }) =>
    theme.accordionOpen &&
    css`
      animation: ${openContentAnimation} 0.7s ease-in-out forwards;

      ${() =>
        theme.expose &&
        theme.visualDebug &&
        css`
          max-height: 1000px;
        `}
    `};
`;

export const AccordionContentSpacer = styled.div`
  padding-bottom: ${accordionTokens.ContentSpacingPaddingDefaultBottom};
  padding-left: ${accordionTokens.ContentSpacingPaddingDefaultLeft};
  padding-right: ${accordionTokens.ContentSpacingPaddingDefaultRight};
  padding-top: ${accordionTokens.ContentSpacingPaddingDefaultTop};
  visibility: hidden;

  ${({ theme }) =>
    theme.accordionOpen &&
    css`
      visibility: visible;
    `};
`;
